import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Sparkles, Loader2, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../lib/api";

const SESSION_KEY = "gsp_chat_session_id";
const HISTORY_KEY = "gsp_chat_open";

const getSessionId = () => {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

const starterPrompts = [
  "What does Shivani do?",
  "Can she help my D2C brand?",
  "Show me your best case study",
  "How do I book a strategy call?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, content}
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", email: "" });
  const [leadSending, setLeadSending] = useState(false);
  const scrollRef = useRef(null);

  // Load history on mount
  useEffect(() => {
    const sid = getSessionId();
    fetch(`${API}/chat/history/${encodeURIComponent(sid)}`)
      .then((r) => r.ok ? r.json() : [])
      .then((h) => setMessages(h.map((m) => ({ role: m.role, content: m.content }))))
      .catch(() => {});
    // Restore open state
    if (localStorage.getItem(HISTORY_KEY) === "1") setOpen(true);
  }, []);

  useEffect(() => {
    localStorage.setItem(HISTORY_KEY, open ? "1" : "0");
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    const sid = getSessionId();
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid, message: text }),
      });
      if (!res.body) throw new Error("no stream body");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let done = false;
      while (!done) {
        const { value, done: rDone } = await reader.read();
        done = rDone;
        if (value) {
          buf += decoder.decode(value, { stream: true });
          // Process SSE frames separated by \n\n
          let idx;
          while ((idx = buf.indexOf("\n\n")) !== -1) {
            const frame = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            const lines = frame.split("\n");
            let event = "message";
            let data = "";
            for (const line of lines) {
              if (line.startsWith("event:")) event = line.slice(6).trim();
              else if (line.startsWith("data:")) data += line.slice(5).replace(/^ /, "");
            }
            if (event === "error") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: `⚠️ ${data || "Something went wrong."}` };
                return copy;
              });
              done = true;
              break;
            } else if (event === "done") {
              done = true;
              break;
            } else if (event === "message" && data) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: (copy[copy.length - 1].content || "") + data };
                return copy;
              });
            }
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "⚠️ Couldn't reach the assistant. Please try again shortly." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!leadForm.name || !leadForm.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLeadSending(true);
    try {
      const transcript = messages
        .filter((m) => m.content)
        .map((m) => `${m.role === "user" ? "Visitor" : "AI Assistant"}: ${m.content}`)
        .join("\n\n");
      await axios.post(`${API}/contact`, {
        name: leadForm.name,
        email: leadForm.email,
        company: "",
        message: `[From chat widget]\n\n${transcript || "(No conversation yet — visitor requested contact from the chat panel.)"}`,
      });
      toast.success("Sent — Shivani will follow up personally within 24h.");
      setLeadOpen(false);
      setLeadForm({ name: "", email: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send. Try again.");
    } finally {
      setLeadSending(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="chat-toggle"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-20 right-5 z-[60] inline-flex items-center gap-2 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] text-white shadow-[0_10px_40px_-10px_rgba(59,130,246,0.7)] px-4 py-3 transition"
        aria-label="Open AI Growth Assistant"
      >
        {open ? <X size={18} /> : <Sparkles size={18} />}
        <span className="text-sm font-medium hidden sm:inline">{open ? "Close" : "Ask AI"}</span>
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-36 right-4 sm:right-5 z-[59] w-[calc(100vw-2rem)] sm:w-[420px] max-h-[calc(100vh-11rem)] rounded-2xl overflow-hidden bg-[#0F1115] text-white border border-white/10 shadow-2xl flex flex-col"
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-white/5 bg-[#0F1115]/95">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#10B981] pulse-dot" />
              <div>
                <div className="font-display text-base leading-none">AI Growth Assistant</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 mt-1">Powered by GPT-5.2 · Beta</div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.length === 0 && (
              <div>
                <div className="rounded-xl bg-white/[0.03] border border-white/5 p-4 text-sm text-white/80">
                  Hi 👋 I&apos;m Shivani&apos;s AI Growth Assistant. Ask me about her services, case studies, or how she can help your business grow.
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2">
                  {starterPrompts.map((p) => (
                    <button
                      key={p}
                      data-testid={`chat-starter-${p.slice(0, 12).replace(/\s/g, "-").toLowerCase()}`}
                      onClick={() => send(p)}
                      className="text-left text-xs px-3 py-2 rounded-lg border border-white/10 bg-white/[0.02] hover:border-[#3B82F6] hover:bg-[#3B82F6]/10 transition"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#3B82F6] text-white"
                      : "bg-white/[0.04] border border-white/5 text-white/90"
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? <Loader2 size={14} className="animate-spin opacity-60" /> : null)}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-white/5 p-3 flex items-center gap-2 bg-[#0F1115]"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about services, case studies, results…"
              disabled={streaming}
              className="flex-1 bg-white/[0.03] border border-white/10 focus:border-[#3B82F6] outline-none rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={streaming || !input.trim()}
              data-testid="chat-send"
              className="inline-flex items-center justify-center rounded-lg bg-white text-neutral-900 w-9 h-9 disabled:opacity-40"
            >
              {streaming ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </form>

          {/* Lead capture strip */}
          <div className="px-3 pb-3 bg-[#0F1115]">
            {!leadOpen ? (
              <button
                data-testid="chat-request-followup"
                onClick={() => setLeadOpen(true)}
                className="w-full inline-flex items-center justify-between gap-2 rounded-lg border border-[#3B82F6]/30 bg-[#3B82F6]/10 hover:bg-[#3B82F6]/20 text-[#93C5FD] px-3 py-2.5 text-xs font-medium transition"
              >
                <span className="inline-flex items-center gap-2"><Mail size={12} /> Want Shivani to follow up personally?</span>
                <ArrowRight size={12} />
              </button>
            ) : (
              <form data-testid="chat-lead-form" onSubmit={submitLead} className="rounded-lg border border-white/10 bg-white/[0.03] p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#93C5FD]">Send transcript to Shivani</div>
                  <button type="button" onClick={() => setLeadOpen(false)} className="text-white/40 hover:text-white"><X size={12} /></button>
                </div>
                <input
                  data-testid="chat-lead-name"
                  value={leadForm.name}
                  onChange={(e) => setLeadForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Your name"
                  className="w-full bg-white/[0.03] border border-white/10 focus:border-[#3B82F6] outline-none rounded-md px-3 py-2 text-xs text-white placeholder-white/30"
                />
                <input
                  data-testid="chat-lead-email"
                  type="email"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="you@company.com"
                  className="w-full bg-white/[0.03] border border-white/10 focus:border-[#3B82F6] outline-none rounded-md px-3 py-2 text-xs text-white placeholder-white/30"
                />
                <button
                  type="submit"
                  data-testid="chat-lead-submit"
                  disabled={leadSending}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-white text-neutral-900 px-3 py-2 text-xs font-medium disabled:opacity-60"
                >
                  {leadSending ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
                  {leadSending ? "Sending…" : "Send transcript"}
                </button>
              </form>
            )}
          </div>

          <div className="px-3 pb-3 text-[10px] font-mono text-white/30 text-center">
            Answers may be approximate — confirm details with Shivani directly.
          </div>
        </div>
      )}
    </>
  );
}
